import { useState, useEffect } from "react";
import { Brain, TrendingUp, AlertTriangle, Loader2 } from "lucide-react";
import { estimateAIUsage } from "@/lib/openaiClient";

interface AIMonitorProps {
  content: string;
  onAIUsageUpdate: (percentage: number) => void;
}

export default function AIMonitor({ content, onAIUsageUpdate }: AIMonitorProps) {
  const [aiPercentage, setAiPercentage] = useState(0);
  const [originalPercentage, setOriginalPercentage] = useState(100);
  const [recentActivity, setRecentActivity] = useState<Array<{ type: string; time: string }>>([]);
  const [rationale, setRationale] = useState("");
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const aiAssistAvailable = Boolean(
    (import.meta.env.VITE_SUPABASE_FUNCTIONS_URL ?? import.meta.env.VITE_SUPABASE_URL) &&
    import.meta.env.VITE_SUPABASE_ANON_KEY
  );

  const runHeuristicAnalysis = () => {
    if (!content) {
      setAiPercentage(0);
      setOriginalPercentage(100);
      onAIUsageUpdate(0);
      return;
    }
    const words = content.split(/\s+/).filter(w => w.length > 0);
    const totalWords = words.length;
    const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 0);
    if (sentences.length === 0) {
      setAiPercentage(0);
      setOriginalPercentage(100);
      onAIUsageUpdate(0);
      return;
    }
    const avgSentenceLength = totalWords / sentences.length;
    const variance = sentences.reduce((sum, s) => {
      const len = s.split(/\s+/).length;
      return sum + Math.pow(len - avgSentenceLength, 2);
    }, 0) / sentences.length;
    const aiScore = variance < 10 ? 30 : variance < 20 ? 15 : 5;
    setAiPercentage(Math.min(aiScore, 100));
    setOriginalPercentage(Math.max(0, 100 - aiScore));
    setRationale("Heuristic estimate based on sentence uniformity.");
    onAIUsageUpdate(Math.min(aiScore, 100));
  };

  useEffect(() => {
    if (!content) {
      setAiPercentage(0);
      setOriginalPercentage(100);
      setRationale("");
      setAiError(null);
      return;
    }

    if (!aiAssistAvailable) {
      runHeuristicAnalysis();
      setAiError(null);
      setLoadingAI(false);
      return;
    }

    let active = true;
    setLoadingAI(true);
    setAiError(null);

    const timer = setTimeout(async () => {
      try {
        const result = await estimateAIUsage({ content });
        if (!active) return;
        setAiPercentage(result.aiPercentage);
        setOriginalPercentage(result.originalPercentage);
        setRationale(result.rationale);
        onAIUsageUpdate(result.aiPercentage);
      } catch (err) {
        if (!active) return;
        console.error("AI usage estimation failed", err);
        setAiError("Live AI check unavailable. Showing heuristic estimate.");
        runHeuristicAnalysis();
      } finally {
        if (active) setLoadingAI(false);
      }
    }, 400);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [content, aiAssistAvailable, onAIUsageUpdate]);

  useEffect(() => {
    // Add activity log entry
    if (content.length > 0) {
      const now = new Date();
      const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      setRecentActivity(prev => [
        { type: "Manual typing detected", time: timeStr },
        ...prev.slice(0, 4)
      ]);
    }
  }, [content.length]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 mb-4">
        <Brain className="w-5 h-5 text-white/90" />
        <h3 className="text-lg font-semibold text-white">AI Monitor</h3>
      </div>

      <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 mb-4 shadow-lg">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-white/70">Original Work</span>
            <span className="text-lg font-semibold text-green-400">{originalPercentage}%</span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-green-400 to-emerald-500 transition-all duration-300"
              style={{ width: `${originalPercentage}%` }}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-white/70">AI Assistance</span>
            <span className="text-lg font-semibold text-purple-400">{aiPercentage}%</span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-400 to-pink-500 transition-all duration-300"
              style={{ width: `${aiPercentage}%` }}
            />
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-white/20">
          <div className="flex items-center gap-2">
            {originalPercentage >= 80 ? (
              <>
                <TrendingUp className="w-4 h-4 text-green-400" />
                <span className="text-xs text-green-400">Highly original content</span>
              </>
            ) : originalPercentage >= 50 ? (
              <>
                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                <span className="text-xs text-yellow-400">Moderate AI assistance detected</span>
              </>
            ) : (
              <>
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-xs text-red-400">High AI usage detected</span>
              </>
            )}
          </div>
          <p className="text-[11px] text-white/50 mt-2">
            {loadingAI ? (
              <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Running deep analysis...</span>
            ) : (
              rationale || "Heuristic estimate."
            )}
          </p>
          {aiError && !loadingAI && (
            <p className="text-[11px] text-yellow-300 mt-1">{aiError}</p>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <h4 className="text-sm font-medium text-white/90 mb-3">Recent Activity</h4>
        <div className="space-y-2">
          {recentActivity.map((activity, idx) => (
            <div
              key={idx}
              className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 shadow-lg"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-white/80">{activity.type}</span>
                <span className="text-xs text-white/50">{activity.time}</span>
              </div>
            </div>
          ))}

          {recentActivity.length === 0 && (
            <div className="text-center py-8 text-white/50">
              <Brain className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-xs">No activity yet</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3 shadow-lg">
        <p className="text-xs text-white/70 text-center">
          This feature helps ensure academic integrity by monitoring writing patterns.
        </p>
      </div>
    </div>
  );
}
